import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import Alert from "../../../components/common/Alert";
import Modal from "../../../components/common/Modal";
import { useAuth } from "../../../contexts/AuthContext";
import MessagingService from "../../../services/core/MessagingService";

const MAILBOX_INBOX = "E";
const MAILBOX_SENT = "S";
const MAILBOX_RECEIVED = MAILBOX_INBOX;

const initialCompose = {
  title: "",
  body: "",
  critical: false,
  responseRequired: false,
  recipients: [],
  recipientUserIds: [],
  recipientGroupIds: [],
  sendAsSystem: false,
  attachments: [],
  originMessageId: null,
};

const fileToAttachment = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const value = String(reader.result || "");
      resolve({
        fileName: file.name,
        contentType: file.type || "application/octet-stream",
        contentBase64: value.includes(",") ? value.split(",").pop() : value,
      });
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

const formatDate = (value) => {
  if (!value) return "";
  return new Intl.DateTimeFormat(undefined, {
    dateStyle: "short",
    timeStyle: "short",
  }).format(new Date(value));
};

const userNames = (users, type, t, systemSender = false) => {
  if (systemSender && type === MAILBOX_SENT) {
    return t("messaging.systemSender");
  }
  return (users || [])
    .filter((user) => user.type === type)
    .map((user) => user.displayName || user.email)
    .filter(Boolean)
    .join(", ");
};

const MessagingPage = () => {
  const { t } = useTranslation();
  const { hasPermission, isSuperuser } = useAuth();
  const canCreateMessages = hasPermission("messaging.create");
  const canUpdateMessages = hasPermission("messaging.update");
  const [mailbox, setMailbox] = useState(MAILBOX_INBOX);
  const [messages, setMessages] = useState([]);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [users, setUsers] = useState([]);
  const [groups, setGroups] = useState([]);
  const [search, setSearch] = useState("");
  const [recipientSearch, setRecipientSearch] = useState("");
  const [groupSearch, setGroupSearch] = useState("");
  const [composeTenantFilter, setComposeTenantFilter] = useState("");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [composeOpen, setComposeOpen] = useState(false);
  const [compose, setCompose] = useState(initialCompose);
  const [groupOpen, setGroupOpen] = useState(false);
  const [groupForm, setGroupForm] = useState({ name: "", description: "", tenantId: "", memberUserIds: [] });
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const selectedMailboxEntry = useMemo(() => {
    if (!selectedMessage) return null;
    return (selectedMessage.users || []).find((user) => user.type === mailbox);
  }, [mailbox, selectedMessage]);

  const loadMailbox = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const page = await MessagingService.mailbox({ type: mailbox, search, page: 0, size: 50 });
      const content = page?.content || [];
      setMessages(content);
      if (selectedId && !content.some((message) => message.id === selectedId)) {
        setSelectedId(null);
        setSelectedMessage(null);
      }
    } catch (loadError) {
      setError(loadError?.message || t("messaging.errors.load"));
    } finally {
      setLoading(false);
    }
  }, [mailbox, search, selectedId, t]);

  const loadUsers = useCallback(async () => {
    try {
      setUsers(await MessagingService.users());
    } catch {
      setUsers([]);
    }
  }, []);

  const loadGroups = useCallback(async () => {
    try {
      setGroups(await MessagingService.groups());
    } catch {
      setGroups([]);
    }
  }, []);

  useEffect(() => {
    loadUsers();
    loadGroups();
  }, [loadGroups, loadUsers]);

  useEffect(() => {
    loadMailbox();
  }, [loadMailbox]);

  useEffect(() => {
    return MessagingService.subscribe((payload, eventName) => {
      if (eventName === "message-counts" && mailbox === MAILBOX_INBOX) {
        loadMailbox();
      }
    });
  }, [loadMailbox, mailbox]);

  const openDetail = async (message) => {
    setSelectedId(message.id);
    setError("");
    try {
      const detail = await MessagingService.detail(message.id, false);
      setSelectedMessage(detail);
      const entry = (detail.users || []).find((user) => user.type === MAILBOX_INBOX);
      if (mailbox === MAILBOX_INBOX && entry && !entry.read) {
        await MessagingService.markRead(entry.id);
        loadMailbox();
      }
    } catch (detailError) {
      setError(detailError?.message || t("messaging.errors.detail"));
    }
  };

  const deleteSelected = async () => {
    if (!selectedMailboxEntry?.id) return;
    try {
      await MessagingService.updateEntry(selectedMailboxEntry.id, { deleted: true });
      setSelectedId(null);
      setSelectedMessage(null);
      await loadMailbox();
    } catch (deleteError) {
      setError(deleteError?.message || t("messaging.errors.delete"));
    }
  };

  const startReply = () => {
    if (!selectedMessage) return;
    const sender = (selectedMessage.users || []).find((user) => user.type === MAILBOX_SENT);
    setCompose({
      ...initialCompose,
      title: selectedMessage.title?.toLowerCase().startsWith("re:")
        ? selectedMessage.title
        : `Re: ${selectedMessage.title || ""}`,
      recipients: sender?.userId ? [{ tenantId: sender.tenantId || null, userId: sender.userId }] : [],
      recipientUserIds: [],
      recipientGroupIds: [],
      sendAsSystem: false,
      originMessageId: selectedMessage.id,
    });
    setRecipientSearch("");
    setGroupSearch("");
    setComposeTenantFilter("");
    setComposeOpen(true);
  };

  const submitCompose = async (event) => {
    event.preventDefault();
    setSaving(true);
    setError("");
    try {
      await MessagingService.create(compose);
      setCompose(initialCompose);
      setComposeOpen(false);
      setSuccess(t("messaging.success.sent"));
      await loadMailbox();
    } catch (saveError) {
      setError(saveError?.message || t("messaging.errors.send"));
    } finally {
      setSaving(false);
    }
  };

  const handleFiles = async (event) => {
    const files = Array.from(event.target.files || []);
    const attachments = await Promise.all(files.map(fileToAttachment));
    setCompose((prev) => ({ ...prev, attachments }));
  };

  const recipientKey = (user) => `${user.tenantId || ""}:${user.userId}`;

  const toggleRecipient = (user) => {
    if (user && !user.messagingEnabled) {
      setError(t("messaging.errors.recipientNoPermission", {
        name: user.displayName || user.email,
      }));
      return;
    }
    setCompose((prev) => {
      const key = recipientKey(user);
      const exists = prev.recipients.some((item) => `${item.tenantId || ""}:${item.userId}` === key);
      return {
        ...prev,
        recipients: exists
          ? prev.recipients.filter((item) => `${item.tenantId || ""}:${item.userId}` !== key)
          : [...prev.recipients, { tenantId: user.tenantId || null, userId: user.userId }],
      };
    });
  };

  const toggleGroup = (groupId) => {
    setCompose((prev) => {
      const exists = prev.recipientGroupIds.includes(groupId);
      return {
        ...prev,
        recipientGroupIds: exists
          ? prev.recipientGroupIds.filter((id) => id !== groupId)
          : [...prev.recipientGroupIds, groupId],
      };
    });
  };

  const submitGroup = async (event) => {
    event.preventDefault();
    setSaving(true);
    setError("");
    try {
      const created = await MessagingService.createGroup({
        name: groupForm.name,
        description: groupForm.description,
        tenantId: groupForm.tenantId || null,
        memberUserIds: groupForm.memberUserIds,
      });
      await loadGroups();
      setCompose((prev) => ({
        ...prev,
        recipientGroupIds: [...prev.recipientGroupIds, created.id],
      }));
      setGroupForm({ name: "", description: "", tenantId: "", memberUserIds: [] });
      setGroupOpen(false);
    } catch (saveError) {
      setError(saveError?.message || t("messaging.errors.groupSave"));
    } finally {
      setSaving(false);
    }
  };

  const toggleGroupMember = (userId) => {
    setGroupForm((prev) => {
      const exists = prev.memberUserIds.includes(userId);
      return {
        ...prev,
        memberUserIds: exists
          ? prev.memberUserIds.filter((id) => id !== userId)
          : [...prev.memberUserIds, userId],
      };
    });
  };

  const selectedRecipientCount = compose.recipients.length + compose.recipientGroupIds.length;

  const tenantFilterOptions = useMemo(() => {
    return [...new Map(
      users.filter((user) => user.tenantId).map((user) => [user.tenantId, user.tenantName || user.tenantId])
    ).entries()].sort((a, b) => String(a[1]).localeCompare(String(b[1])));
  }, [users]);

  const filteredUsers = useMemo(() => {
    const term = recipientSearch.trim().toLowerCase();
    return users.filter((user) => {
      if (composeTenantFilter && user.tenantId !== composeTenantFilter) return false;
      if (!term) return true;
      const haystack = `${user.displayName || ""} ${user.email || ""}`.toLowerCase();
      return haystack.includes(term);
    });
  }, [users, recipientSearch, composeTenantFilter]);

  const filteredGroups = useMemo(() => {
    const term = groupSearch.trim().toLowerCase();
    return groups.filter((group) => {
      if (composeTenantFilter && group.tenantId !== composeTenantFilter) return false;
      if (!term) return true;
      return (group.name || "").toLowerCase().includes(term);
    });
  }, [groups, groupSearch, composeTenantFilter]);

  return (
    <div className="messaging-page">
      <div className="page-header-card">
        <div className="page-header-icon">
          <i className="fa fa-envelope"></i>
        </div>
        <div>
          <h1>{t("messaging.title")}</h1>
          <p>{t("messaging.subtitle")}</p>
        </div>
        {canCreateMessages && (
          <button
            type="button"
            className="btn btn-primary messaging-compose-button"
            onClick={() => {
              setCompose(initialCompose);
              setCompose((prev) => ({ ...prev, sendAsSystem: isSuperuser }));
              setRecipientSearch("");
              setGroupSearch("");
              setComposeTenantFilter("");
              setComposeOpen(true);
            }}
          >
            <i className="fa fa-plus"></i>
            {t("messaging.newMessage")}
          </button>
        )}
      </div>

      {error && <Alert type="error" message={error} onClose={() => setError("")} />}
      {success && <Alert type="success" message={success} onClose={() => setSuccess("")} autoClose={2500} />}

      <div className="messaging-toolbar">
        <div className="messaging-tabs">
          <button
            type="button"
            className={mailbox === MAILBOX_INBOX ? "active" : ""}
            onClick={() => {
              setMailbox(MAILBOX_INBOX);
              setSelectedId(null);
              setSelectedMessage(null);
            }}
          >
            <i className="fa fa-inbox"></i>
            {t("messaging.inbox")}
          </button>
          <button
            type="button"
            className={mailbox === MAILBOX_SENT ? "active" : ""}
            onClick={() => {
              setMailbox(MAILBOX_SENT);
              setSelectedId(null);
              setSelectedMessage(null);
            }}
          >
            <i className="fa fa-paper-plane"></i>
            {t("messaging.sent")}
          </button>
        </div>
        <div className="messaging-search">
          <i className="fa fa-search"></i>
          <input
            value={search}
            placeholder={t("messaging.searchPlaceholder")}
            onChange={(event) => setSearch(event.target.value)}
          />
        </div>
      </div>

      <div className="messaging-shell">
        <section className="messaging-list">
          {loading && <div className="messaging-empty">{t("common.loading")}</div>}
          {!loading && messages.length === 0 && (
            <div className="messaging-empty">{t("messaging.empty")}</div>
          )}
          {messages.map((message) => {
            const unread = mailbox === MAILBOX_INBOX && !message.read;
            return (
              <button
                type="button"
                key={message.id}
                className={`messaging-list-item ${selectedId === message.id ? "selected" : ""} ${unread ? "unread" : ""}`}
                onClick={() => openDetail(message)}
              >
                <div className="messaging-list-top">
                  <strong>{mailbox === MAILBOX_INBOX ? userNames(message.users, MAILBOX_SENT, t, message.systemSender) : userNames(message.users, MAILBOX_RECEIVED, t)}</strong>
                  <span>{formatDate(message.date)}</span>
                </div>
                <div className="messaging-list-title">
                  {message.critical && <i className="fa fa-exclamation-circle"></i>}
                  <span>{message.title}</span>
                </div>
                <p>{message.bodyPreview}</p>
                <div className="messaging-list-meta">
                  {message.attachmentCount > 0 && <span><i className="fa fa-paperclip"></i>{message.attachmentCount}</span>}
                  {message.responseRequired && <span><i className="fa fa-reply"></i>{t("messaging.responseRequired")}</span>}
                  {message.responded && <span><i className="fa fa-check"></i>{t("messaging.responded")}</span>}
                </div>
              </button>
            );
          })}
        </section>

        <section className="messaging-detail">
          {!selectedMessage && (
            <div className="messaging-empty detail-empty">
              <i className="fa fa-envelope-open-o"></i>
              <span>{t("messaging.selectMessage")}</span>
            </div>
          )}
          {selectedMessage && (
            <>
              <div className="messaging-detail-header">
                <div>
                  <h2>{selectedMessage.title}</h2>
                  <p>{formatDate(selectedMessage.date)}</p>
                </div>
                <div className="messaging-detail-actions">
                  {mailbox === MAILBOX_INBOX && canCreateMessages && (
                    <button type="button" className="btn btn-secondary" onClick={startReply}>
                      <i className="fa fa-reply"></i>
                      {t("messaging.reply")}
                    </button>
                  )}
                  {canUpdateMessages && (
                    <button type="button" className="btn btn-secondary danger" onClick={deleteSelected}>
                      <i className="fa fa-trash"></i>
                      {t("common.delete")}
                    </button>
                  )}
                </div>
              </div>
              <div className="messaging-people">
                <div><strong>{t("messaging.from")}:</strong> {userNames(selectedMessage.users, MAILBOX_SENT, t, selectedMessage.systemSender)}</div>
                <div><strong>{t("messaging.to")}:</strong> {userNames(selectedMessage.users, MAILBOX_INBOX, t)}</div>
                {selectedMessage.originTitle && (
                  <div><strong>{t("messaging.thread")}:</strong> {selectedMessage.originTitle}</div>
                )}
              </div>
              <div
                className="messaging-body"
                dangerouslySetInnerHTML={{ __html: selectedMessage.body || "" }}
              />
              {selectedMessage.attachments?.length > 0 && (
                <div className="messaging-attachments">
                  <h3>{t("messaging.attachments")}</h3>
                  {selectedMessage.attachments.map((attachment) => (
                    <button
                      key={attachment.id}
                      type="button"
                      onClick={() => MessagingService.downloadAttachment(attachment)}
                    >
                      <i className="fa fa-paperclip"></i>
                      {attachment.fileName}
                    </button>
                  ))}
                </div>
              )}
            </>
          )}
        </section>
      </div>

      <Modal
        isOpen={composeOpen}
        onClose={() => setComposeOpen(false)}
        title={compose.originMessageId ? t("messaging.reply") : t("messaging.newMessage")}
        className="messaging-compose-modal"
      >
        <form className="messaging-compose-form" onSubmit={submitCompose}>
          {tenantFilterOptions.length > 1 && (
            <label>
              {t("messaging.tenantFilterLabel")}
              <select
                value={composeTenantFilter}
                onChange={(event) => setComposeTenantFilter(event.target.value)}
              >
                <option value="">{t("messaging.allTenants")}</option>
                {tenantFilterOptions.map(([tenantId, tenantName]) => (
                  <option key={tenantId} value={tenantId}>{tenantName}</option>
                ))}
              </select>
            </label>
          )}
          <label>
            {t("messaging.recipients")}
            <div className="messaging-search">
              <i className="fa fa-search"></i>
              <input
                value={recipientSearch}
                placeholder={t("messaging.recipientSearchPlaceholder")}
                onChange={(event) => setRecipientSearch(event.target.value)}
              />
            </div>
            <div className="messaging-recipient-list">
              {filteredUsers.map((user) => (
                <button
                  key={recipientKey(user)}
                  type="button"
                  className={[
                    compose.recipients.some((item) => `${item.tenantId || ""}:${item.userId}` === recipientKey(user)) ? "selected" : "",
                    !user.messagingEnabled ? "unavailable" : "",
                  ].filter(Boolean).join(" ")}
                  onClick={() => toggleRecipient(user)}
                  title={!user.messagingEnabled ? t("messaging.errors.recipientNoPermission", {
                    name: user.displayName || user.email,
                  }) : undefined}
                >
                  <i className={`fa ${user.messagingEnabled ? "fa-user" : "fa-user-times"}`}></i>
                  <span>{user.displayName || user.email}</span>
                  {user.tenantName && <small>{user.tenantName}</small>}
                </button>
              ))}
              {filteredUsers.length === 0 && (
                <div className="messaging-empty-inline">{t("common.noResults")}</div>
              )}
            </div>
          </label>
          <label>
            {t("messaging.groups")}
            <div className="messaging-search">
              <i className="fa fa-search"></i>
              <input
                value={groupSearch}
                placeholder={t("messaging.groupSearchPlaceholder")}
                onChange={(event) => setGroupSearch(event.target.value)}
              />
            </div>
            <div className="messaging-recipient-list messaging-group-list">
              {filteredGroups.map((group) => (
                <button
                  key={group.id}
                  type="button"
                  className={compose.recipientGroupIds.includes(group.id) ? "selected" : ""}
                  onClick={() => toggleGroup(group.id)}
                >
                  <i className={`fa ${group.allUsers ? "fa-users" : "fa-object-group"}`}></i>
                  <span>{group.name}</span>
                  <small>{group.tenantName || t("messaging.allTenants")} · {group.memberCount}</small>
                </button>
              ))}
              {filteredGroups.length === 0 && (
                <div className="messaging-empty-inline">{t("common.noResults")}</div>
              )}
            </div>
            <button type="button" className="btn btn-secondary messaging-small-action" onClick={() => setGroupOpen(true)}>
              <i className="fa fa-plus"></i>
              {t("messaging.createGroup")}
            </button>
          </label>
          {isSuperuser && (
            <div className="messaging-compose-options">
              <label>
                <input
                  type="checkbox"
                  checked={compose.sendAsSystem}
                  onChange={(event) => setCompose((prev) => ({ ...prev, sendAsSystem: event.target.checked }))}
                />
                {t("messaging.sendAsSystem")}
              </label>
            </div>
          )}
          <label>
            {t("messaging.subject")}
            <input
              value={compose.title}
              onChange={(event) => setCompose((prev) => ({ ...prev, title: event.target.value }))}
              required
            />
          </label>
          <label>
            {t("messaging.body")}
            <textarea
              value={compose.body}
              onChange={(event) => setCompose((prev) => ({ ...prev, body: event.target.value }))}
              rows={8}
            />
          </label>
          <div className="messaging-compose-options">
            <label>
              <input
                type="checkbox"
                checked={compose.critical}
                onChange={(event) => setCompose((prev) => ({ ...prev, critical: event.target.checked }))}
              />
              {t("messaging.critical")}
            </label>
            <label>
              <input
                type="checkbox"
                checked={compose.responseRequired}
                onChange={(event) => setCompose((prev) => ({ ...prev, responseRequired: event.target.checked }))}
              />
              {t("messaging.responseRequired")}
            </label>
          </div>
          <label>
            {t("messaging.attachments")}
            <input type="file" multiple onChange={handleFiles} />
          </label>
          {compose.attachments.length > 0 && (
            <div className="messaging-compose-files">
              {compose.attachments.map((attachment) => (
                <span key={attachment.fileName}>{attachment.fileName}</span>
              ))}
            </div>
          )}
          <div className="modal-actions">
            <button type="button" className="btn btn-secondary" onClick={() => setComposeOpen(false)}>
              {t("common.cancel")}
            </button>
            <button type="submit" className="btn btn-primary" disabled={saving || !selectedRecipientCount}>
              <i className="fa fa-paper-plane"></i>
              {saving ? t("common.processing") : t("messaging.send")}
            </button>
          </div>
        </form>
      </Modal>

      <Modal
        isOpen={groupOpen}
        onClose={() => setGroupOpen(false)}
        title={t("messaging.createGroup")}
        className="messaging-compose-modal"
      >
        <form className="messaging-compose-form" onSubmit={submitGroup}>
          <label>
            {t("messaging.groupName")}
            <input
              value={groupForm.name}
              onChange={(event) => setGroupForm((prev) => ({ ...prev, name: event.target.value }))}
              required
            />
          </label>
          <label>
            {t("messaging.groupDescription")}
            <input
              value={groupForm.description}
              onChange={(event) => setGroupForm((prev) => ({ ...prev, description: event.target.value }))}
            />
          </label>
          {isSuperuser && (
            <label>
              {t("messaging.groupTenant")}
              <select
                value={groupForm.tenantId}
                onChange={(event) => setGroupForm((prev) => ({ ...prev, tenantId: event.target.value, memberUserIds: [] }))}
              >
                <option value="">{t("messaging.allTenants")}</option>
                {[...new Map(users.filter((user) => user.tenantId).map((user) => [user.tenantId, user.tenantName || user.tenantId])).entries()]
                  .map(([tenantId, tenantName]) => (
                    <option key={tenantId} value={tenantId}>{tenantName}</option>
                  ))}
              </select>
            </label>
          )}
          <label>
            {t("messaging.groupMembers")}
            <div className="messaging-recipient-list">
              {users
                .filter((user) => !groupForm.tenantId || user.tenantId === groupForm.tenantId)
                .map((user) => (
                  <button
                    key={`${user.tenantId || "tenant"}-${user.userId}`}
                    type="button"
                    className={groupForm.memberUserIds.includes(user.userId) ? "selected" : ""}
                    onClick={() => toggleGroupMember(user.userId)}
                  >
                    <i className="fa fa-user"></i>
                    <span>{user.displayName || user.email}</span>
                    {user.tenantName && <small>{user.tenantName}</small>}
                  </button>
                ))}
            </div>
          </label>
          <div className="modal-actions">
            <button type="button" className="btn btn-secondary" onClick={() => setGroupOpen(false)}>
              {t("common.cancel")}
            </button>
            <button type="submit" className="btn btn-primary" disabled={saving || !groupForm.name.trim()}>
              {saving ? t("common.processing") : t("common.save")}
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default MessagingPage;
